import { ChangeEventHandler, FC, FormEventHandler, useCallback, useContext, useEffect, useRef, 
  useLayoutEffect, useState } from 'react'
import { authentication } from '@microsoft/teams-js'
import { Button, Input, Label, Image, Text } from '@fluentui/react-components'
import cx from 'classnames'
//import styles from '../Tab/AppDescription.module.css'

import { identifierPrefix, scopes } from '../../constants'
import { TeamsFxContext } from '../Context'

import './ConfigurableTabAuth.css'
import { useTranslation } from 'react-i18next'
import { ConfigurableTabDetails } from './ConfigurableTabDetails'
import { getPattern, getPlaceholder } from '../DomainRules'

import sharedUrl from '../Tab/sharedUrl'
import config from '../../config'
import { app } from '@microsoft/teams-js'
import { TeamsUserCredential } from '@microsoft/teamsfx'
//import styles from './ConfigurableTab.module.css'

interface ConfigurableTabAuthProps {
  defaultDomain: string | null
  authToken: string | null
  onLoginSuccess: (domain: string | null, nextAuthToken: string | null) => void
  reLogin: boolean
  teamsUserCredential?: TeamsUserCredential
}

export const ConfigurableTabAuth: FC<ConfigurableTabAuthProps> = ({ 
  defaultDomain, authToken, onLoginSuccess, reLogin, teamsUserCredential }) => {
  const { themeString } = useContext(TeamsFxContext)
  const { t } = useTranslation()

  const [domain, setDomain] = useState<string | null>(defaultDomain)
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false)
  const [isEnvSuspended, setIsEnvSuspended] = useState<boolean>(false)
  const [url, setUrl] = useState(sharedUrl.getUrlFromDomain(domain ?? ''));

  const caretRef = useRef(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const sharedBaseUrl = config.sharedUrl;
  const ranGet = useRef(false);
  const [context, setContext] = useState<app.Context | null>(null);
  const [loading, setLoading] = useState(true); 
  const userId =useRef('');

  const fetchIsAwake = async (): Promise<boolean> => {
    try {
      const response = await fetch(`https://${domain}/wakeup`, { method: 'GET', mode: 'no-cors' })
      if (response.ok) {
        return false
      } else {
        if (response.status === 404) throw new Error('404')
        if (response.status === 500) throw new Error('500')

        throw new Error(`${response.status}`)
      }
    } catch (e) {
      console.log(e)
      return true
    }
  }

  const wakeup = async (loginCallback: () => any): Promise<void> => {
    const isAwake = await fetchIsAwake()

    if (isAwake) {
      setIsEnvSuspended(false)
      loginCallback()
      return
    } else {
      setIsEnvSuspended(true)
    }

    const interval = setInterval(async (): Promise<void> => {
      const isAwake = await fetchIsAwake()

      if (isAwake) {
        setIsEnvSuspended(false)
        loginCallback()
        clearInterval(interval)
      }
    }, 5000)
  }

  const checkEnvironmentStatus = useCallback((): void => {
    if (domain) {
      fetch(`https://${domain}/wakeup`)
        .then(response => {
          if (response.status === 404) {
            setIsEnvSuspended(false)
            console.log('Environment is awake!')
          } else {
            setIsEnvSuspended(true)
            console.log('Environment is not awake yet. Retrying...')
          }
        })
        .catch(error => {
          console.error('Error occurred while checking environment status', error)
        })
    }
  }, [domain])

  useEffect(() => {
    const interval = setTimeout(checkEnvironmentStatus, 5000)

    return () => {
      clearInterval(interval)
    }
  }, [checkEnvironmentStatus])

  const handleSubmit: FormEventHandler = (e): void => {
    e.preventDefault()
    if (authToken === null) {
      setIsLoggingIn(true)
      void wakeup(login)
    } else {
      onLoginSuccess(null, null)
    }
  }

  const login = async (): Promise<void> => {
    if (domain !== null) {
      const redirectUri = `${window.location.origin}/authCallback`

      const loginUrl = new URL(`https://${domain}/oauth2/auth`)
      loginUrl.searchParams.append('redirect_uri', redirectUri)
      loginUrl.searchParams.append('response_type', 'code')
      loginUrl.searchParams.append('client_id', `${identifierPrefix}.${domain}`)
      loginUrl.searchParams.append('scope', scopes.join(' '))

      const authenticateUrl = `${window.location.origin}/auth-start?authorization-url=${btoa(loginUrl.toString())}`

      authentication.authenticate({
        url: authenticateUrl
      })
        .then((result) => {
          console.log('Login succeeded: ' + result)

          if (result !== null) {
            onLoginSuccess(domain, result)
          } else {
            throw new Error('No token found in the storage')
          }
        })
        .catch((e) => {
          console.log('Could not authenticate!!!', e)
        })
        .finally(() => {
          setIsLoggingIn(false)
        })
    }
  }

  const initializeTeams = async () => {
  try {
    await app.initialize()
    const teamsContext = await app.getContext()
    setContext(teamsContext)
    setLoading(false)

    console.log('Full Teams Context:', teamsContext)
    return teamsContext.user?.id
    } catch (error) {
      console.error('Failed to initialize Teams:', error)
      setLoading(false)
      return 'testuseronly'
    }
  }

  useEffect(() => {
      if (ranGet.current) return;
      ranGet.current = true;
      
      const initialize = async () => {
          const AAD = await initializeTeams();
          const clientId = sharedUrl.getOrCreateClientId(AAD, sharedBaseUrl);
          console.log('User ID:', clientId);
          userId.current = clientId;
          console.log('Saved User ID:', userId);
          console.log('Getting url (once)');
          try {
              const response = await sharedUrl.getUrl(sharedBaseUrl + userId.current);
              if (response) {
                setUrl(response);
              }
              else {
                console.error('Null string for url');
            }
          } catch (error) {
              console.error('Error fetching string:', error);
          }
      };

    initialize()
  }, [sharedBaseUrl, teamsUserCredential])

  useLayoutEffect(() => {
      const input = inputRef.current;
      if (!input) return;

      const pos = Math.min(caretRef.current, url.length);
      input.setSelectionRange(pos, pos);
    }, [url]);

  const enteredUrl = (e: any) => {

    const input = e.target;
    caretRef.current = input.selectionStart;

    let _url = e.target.value;
    setUrl(_url);
    let _domain: string = _url.replace('https://', '');
    setDomain (_domain); // strip off https:// as domain code expects just domain
    localStorage.setItem('domain', _domain ?? '')
  }

  return (
    <div
      className={cx('home', {
        light: themeString === 'default',
        dark: themeString === 'dark',
        contrast: themeString !== 'default' && themeString !== 'dark'
      })}
    >
      <div id='configurable-auth-screen'>
        <form onSubmit={handleSubmit} id='configurable-tab-sign-in-form'>

        {reLogin === true
        ? <div id='reLoginPanel'>

            <div id='logo'>
              <Image src='projectadvantage-logo-name.png' id='image'/>
            </div>

            <div id='boards'>
              <Image src='boards-icon.png' id='image'/>
              <div id='label'><Text >{t('BOARDS_DESCRIPTION')}</Text></div>              
            </div>    
                    
          </div>
        : <></>}
          {reLogin === true ? <Label>{t('LOGIN_DESC_LINE_1')}</Label> : <></>}
          <Label>{t('LOGIN_DESC_LINE_2')}</Label>
          <div id='configurable-tab-sign-in-form--input-wrapper'>
            <Input
              required
              ref={inputRef}
              value={url}
               onChange={(e) => enteredUrl(e)}
              type='text'
              pattern={getPattern(true)}
              placeholder={getPlaceholder(true)}
            />
            {authToken !== null
              ? <Button type='submit'>{t('LOGOUT')}</Button>
              : <Button type='submit' onClick={sharedUrl.sendUrl(url, userId, sharedBaseUrl)} disabled={isLoggingIn}>{t('LOGIN')}</Button>}
          </div>
        </form>
      </div>
      {authToken !== null
        ? <ConfigurableTabDetails defaultDomain={domain} authToken='' />
        : <></>}
    </div>
  )
}